import { formatNumber } from "accounting"
import {
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  TimeScale,
  Title,
  Tooltip
} from "chart.js"
import dayjs from "dayjs"

import "chartjs-adapter-moment"

import { formatCurrency } from "../../../Utils/utils"
import { baseOptions } from "../../DashboardPage/components/chartsOptions"
import { getGradient } from "../../DashboardPage/components/chartsPlagins"
import { externalDarkTooltip } from "../../DashboardPage/components/chartTooltips"

ChartJS.register(TimeScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler)

const TOOLTIP_DATE_FORMAT = "MMM D, YYYY"

const LINE_COLOR = "#225AEA"
const COMPARISON_LINE_COLOR = "#A6B6E8"

// For the two single-series charts. The rate chart is a different shape and reuses Health's
// `healthOverviewChartOptions`.
export const homeChartOptions = ({ theme, title, isCurrency }) => {
  const base = baseOptions(theme)
  const formatValue = (value) =>
    isCurrency ? formatCurrency(value ?? 0, true) : formatNumber(value ?? 0, 0, ",", ".")

  const headerStyle = "font-weight:500; color:#7A7A83; padding-bottom:2px;"
  const totalStyle = "font-weight:500; color:white; padding:6px 0;"
  const compareRowStyle =
    "display:flex; align-items:center; gap:8px; color:white; font-weight:500; padding:6px 0;"
  const dashStyle =
    `display:inline-block; width:14px; height:0; border-top:1.5px dashed ${COMPARISON_LINE_COLOR};` +
    " flex-shrink:0;"

  return {
    ...base,
    interaction: { mode: "index", intersect: false },
    plugins: {
      "vertical-line-plugin": { theme },
      title: { display: false },
      legend: { display: false },
      tooltip: {
        enabled: false,
        callbacks: {
          title: (context) => {
            const point = context?.[0]
            const date = point?.label ? dayjs(point.label).format(TOOLTIP_DATE_FORMAT) : ""

            const lines = [
              `__RAW__<div style="${headerStyle}">${title}</div>`,
              `__RAW__<div style="${headerStyle}">${date}</div>`,
              `__RAW__<div style="${totalStyle}">${formatValue(point?.parsed?.y)}</div>`
            ]

            // The dashed line has no legend to explain it, so the tooltip is the only place the
            // comparison is named — by its own day, not the hovered one (see buildComparisonDataset).
            const comparisonPoint = context?.find((entry) => entry.dataset?.isComparison)
            if (comparisonPoint) {
              const rawDate = comparisonPoint.dataset?.comparisonLabels?.[comparisonPoint.dataIndex]
              const comparisonDate = rawDate
                ? dayjs(rawDate).format(TOOLTIP_DATE_FORMAT)
                : comparisonPoint.dataset?.label || ""

              lines.push(
                `__RAW__<div style="${headerStyle} padding-top:8px;">${comparisonDate}</div>`
              )
              lines.push(`<span>${formatValue(comparisonPoint.parsed?.y)}</span></div>`)
            }

            return lines
          }
        },
        external: externalDarkTooltip({ theme })
      }
    },
    scales: {
      ...base.scales,
      x: {
        ...base.scales.x,
        ticks: { ...base.scales.x.ticks, maxTicksLimit: 10, autoSkip: true, maxRotation: 0 }
      },
      y: {
        ...base.scales.y,
        // Both units are whole — a fractional tick prints the same label twice, which is what an
        // empty period used to show ("1 1 1 0 0 0" for ticks 0.6 … 0).
        ticks: { ...base.scales.y.ticks, precision: 0, callback: (value) => formatValue(value) }
      }
    }
  }
}

export const buildPrimaryDataset = ({ label, data, pointCount }) => ({
  label,
  data,
  borderColor: LINE_COLOR,
  borderWidth: 2,
  yAxisID: "y",
  fill: true,
  backgroundColor: (context) => {
    const { ctx, chartArea } = context.chart
    if (!chartArea) return undefined
    return getGradient(ctx, chartArea)
  },
  ...(pointCount === 1
    ? { pointRadius: 4, pointHoverRadius: 6 }
    : { pointRadius: 0, pointHoverRadius: 6 }),
  pointHoverBorderWidth: 2,
  pointHoverBackgroundColor: "white"
})

// `comparisonLabels` carries each point's own calendar day (e.g. the same day one month back):
// the series is plotted against the current axis by index, so that date is nowhere else to be read.
const buildComparisonDataset = ({ label, data, comparisonLabels }) => ({
  label,
  data,
  isComparison: true,
  comparisonLabels,
  borderColor: COMPARISON_LINE_COLOR,
  borderWidth: 1.5,
  borderDash: [6, 6],
  pointRadius: 0,
  pointHoverRadius: 0,
  yAxisID: "y",
  fill: false,
  order: 2
})

// Trustworthy only point-for-point with the axis; a mismatched length plots the dashed line against
// the wrong days, so it is dropped rather than guessed at. The backend sends no series at all for a
// custom compare, which lands here as the same empty overlay.
export const buildComparisonOverlay = ({ labels, series, comparisonLabels, label }) => {
  if (!series?.length || series.length !== labels?.length) return []

  return [buildComparisonDataset({ label, data: series, comparisonLabels })]
}
